import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { useUser } from "@/components/features/users/hooks/use-user";
import { getCoverImage } from "@/lib/image/usage";
import { routes } from "@/lib/routes";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { LifeBuoy, LogIn, LogOut, User2 } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { AccountSwitcher } from "../../account-switcher";
import { useAccountNavItems } from "../hooks/use-nav-items";
import { AccountMenuProps } from "../types";
import { DropdownNavItem } from "./nav-item-renderers";

export function AccountMenu({ className }: AccountMenuProps) {
  const { user, isAuthenticated } = useAuth();
  const { data: userData } = useUser(user?.uid || "", isAuthenticated);
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);
  const activeOrg = userData?.userProfile?.activeOrg;
  const accountNavItems = useAccountNavItems(setAccountSwitcherOpen);

  const displayName = useMemo(() => {
    if (!user) return "Local user";

    const activeOrg = userData?.userProfile?.activeOrg;
    if (activeOrg) {
      return activeOrg.name;
    }
    if (userData?.userProfile?.username) {
      return `@${userData.userProfile.username}`;
    }

    return userData?.userProfile?.firstName ?? user.email;
  }, [userData, user]);

  const coverImage = activeOrg
    ? activeOrg?.imageUrl
    : getCoverImage(userData?.userProfile?.images || [])?.url;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn("hidden md:block", className)}
          asChild
        >
          <Button variant="ghost" size="sm" className="h-auto w-auto p-2">
            {coverImage ? (
              <Avatar className="size-8">
                <AvatarImage
                  className="rounded-full border-2 border-white"
                  src={coverImage}
                  alt={userData?.userProfile?.username || ""}
                />
                <AvatarFallback>
                  {userData?.userProfile?.username?.[0] || "U"}
                </AvatarFallback>
              </Avatar>
            ) : (
              <User2 className="size-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-background/50 w-56 backdrop-blur-xl">
          {isAuthenticated && (
            <>
              <DropdownMenuGroup>
                <DropdownMenuLabel className="w-full truncate font-medium">
                  {displayName}
                </DropdownMenuLabel>
                {accountNavItems.map((item) => (
                  <DropdownNavItem key={item.name} item={item} />
                ))}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem asChild>
            <Link href={routes.support}>
              <LifeBuoy className="h-4 w-4" />
              Support
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            {isAuthenticated ? (
              <Link href={routes.auth.signOut}>
                <LogOut className="h-4 w-4" />
                Logout
              </Link>
            ) : (
              <Link href={routes.auth.signIn}>
                <LogIn className="h-4 w-4" />
                Login
              </Link>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {isAuthenticated && (
        <AccountSwitcher
          open={accountSwitcherOpen}
          onOpenChange={setAccountSwitcherOpen}
        />
      )}
    </>
  );
}
